import React, { useState } from "react";
import {
  Phone, PhoneSlash, MapPin, CheckCircle, XCircle, Clock, ClockCountdown,
  CaretDown, PencilSimple, MagnifyingGlass, Headset,
  EnvelopeSimple, User, Fire, ClockCounterClockwise, Sparkle,
} from "@phosphor-icons/react";
import { STATUSES, STATUS_BY_ID } from "./callListConstants";
import AssistPanel from "./AssistPanel";
import ActionInfoPopover from "./ActionInfoPopover";
import CrmHistoryModal from "./CrmHistoryModal";
import QualityTierBadge from "./QualityTierBadge";
import { useStatusSuggestion } from "./useStatusSuggestion";
import { NOTE_TEMPLATES } from "./noteTemplates";
import { CopyChip } from "./CopyChip";
import FicheShareLinks from "./FicheShareLinks";
import EmailPreviewModal from "./EmailPreviewModal";
import { MicButton } from "./MicButton";
import { DuplicateAlert } from "./DuplicateAlert";
import PostCallAIModal from "./PostCallAIModal";
import ObjectionCoachChip from "./ObjectionCoachChip";
import PronounceButton from "./PronounceButton";
import QualifyStatusModal, { QUALIFY_STATUSES, REFUSAL_OPTIONS } from "./QualifyStatusModal";
import SalesScriptModal from "./SalesScriptModal";
import WebsiteLeadModal from "./WebsiteLeadModal";
import CallbackSuggestion from "./CallbackSuggestion";

// ISO (UTC) → valeur locale pour <input type="datetime-local">
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function QuickBtn({ children, label, onClick, testid, colorCls }) {
  return (
    <button
      onClick={onClick}
      title={label}
      data-testid={testid}
      className={`border bg-card rounded-sm px-2.5 py-1.5 text-[11px] uppercase tracking-wide font-bold inline-flex items-center gap-1.5 transition-colors min-h-[36px] ${colorCls}`}
    >
      {children}
      <span>{label}</span>
    </button>
  );
}

export default function BusinessRow({ biz, telepro, onUpdate, isEditingNote, onStartEditNote, onCancelEditNote, assistOpen, onToggleAssist, onStartCall }) {
  const status = STATUS_BY_ID[biz.call.status] || STATUSES[0];
  const Icon = status.icon;
  const [noteDraft, setNoteDraft] = useState(biz.call.note || "");
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [mailTo, setMailTo] = useState("");
  // VAGUE 21 — Réponses officielles aux objections détectées dans la note
  const [objMatches, setObjMatches] = useState([]);
  const [objLoading, setObjLoading] = useState(false);
  const [qualify, setQualify] = useState(null);
  const [scriptOpen, setScriptOpen] = useState(false);
  const [webLead, setWebLead] = useState(null);
  // Vague 3 — status suggestion based on the note the télépro is typing
  const suggestion = useStatusSuggestion(isEditingNote ? noteDraft : "");
  const phoneHref = biz.phone ? `tel:${biz.phone.replace(/\s/g, "")}` : null;

  // VAGUE 42 — refus / intéressé passent par la qualification (motif ou offre)
  const setStatus = (id, jumpNext = false) => {
    if (QUALIFY_STATUSES.has(id)) return setQualify({ status: id, jumpNext });
    if (id === "site_web_besoin") return setWebLead({ jumpNext });
    return onUpdate({ status: id }, { jumpNext });
  };
  const saveNote = (note) => { onUpdate({ note }); onCancelEditNote(); };
  const saveCallback = (callback_at) => onUpdate({ callback_at });

  const markDnc = () => {
    if (!window.confirm(`« Ne plus contacter » — ${biz.name}\n\nCette entreprise sera bloquée pour TOUS les téléprospecteurs et exclue des futures listes d'appels.\n\nConfirmer ?`)) return;
    onUpdate({ status: "refused_final", refusal_reason: "no_more_contact" });
  };

  return (
    <li className={`flex flex-col transition ${status.rowBg} ${biz.is_callback_due_today ? "ring-2 ring-secondary/50" : ""}`} data-testid={`call-row-${biz.id}`}>
      <div className="px-3 py-3 sm:px-4 sm:py-3 flex flex-col sm:flex-row sm:items-start gap-3">
        <div className="flex-1 min-w-0">
          {biz.is_callback_due_today && (
            <div
              className="mb-1.5 inline-flex flex-wrap items-center gap-1 bg-secondary/15 border border-secondary rounded-sm px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-secondary animate-pulse"
              data-testid={`callback-due-badge-${biz.id}`}
              title={biz.call?.callback_reason || "Rappel programmé pour aujourd'hui"}
            >
              🔔 À RAPPELER AUJOURD'HUI
              {biz.call?.callback_at && (
                <span className="normal-case tracking-normal font-mono text-secondary/90 text-[10px] ml-1">
                  · {new Date(biz.call.callback_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
              {biz.call?.callback_reason && (
                <span className="normal-case tracking-normal font-normal text-secondary/80 text-[10px] ml-1 truncate max-w-[220px]">
                  « {biz.call.callback_reason} »
                </span>
              )}
            </div>
          )}
          {biz.recent_calls_by_others?.length > 0 && (
            <DuplicateAlert
              calls={biz.recent_calls_by_others}
              testid={`duplicate-alert-${biz.id}`}
            />
          )}
          {biz.priority_reasons?.length > 0 && (
            <div
              className="mb-1.5 inline-flex flex-wrap items-center gap-1 bg-secondary/10 border border-secondary/40 rounded-sm px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-secondary"
              data-testid={`priority-badge-${biz.id}`}
              title={biz.priority_reasons.map((r) => r.label).join(" · ")}
            >
              <Fire size={10} weight="fill"/> Priorité
              <span className="normal-case tracking-normal font-normal text-secondary/90 text-[10px] ml-1">
                {biz.priority_reasons.map((r) => r.label).join(" · ")}
              </span>
            </div>
          )}
          {/* Vague 14 — Badge carryover "untouched" : le télépro voit clairement
              pourquoi ce prospect ré-apparaît (pas de log précédent).
              Le badge callback est déjà géré via is_callback_due_today. */}
          {biz.carryover_kind === "untouched" && biz.call?.status === "to_call" && (
            <div
              className="mb-1.5 inline-flex flex-wrap items-center gap-1 bg-[#E0B043]/15 border border-[#E0B043]/50 rounded-sm px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-[#8a6a14]"
              data-testid={`carryover-badge-${biz.id}`}
              title="Ce prospect était dans ta liste précédente mais tu ne l'as pas encore appelé (aucun statut logué)."
            >
              <ClockCounterClockwise size={10} weight="bold"/>
              {biz.carryover_days_old === 1
                ? "Reçu hier"
                : biz.carryover_days_old > 1
                ? `Reçu il y a ${biz.carryover_days_old} j`
                : "À finir"}
              <span className="normal-case tracking-normal font-normal text-[#8a6a14]/85 text-[10px] ml-1">
                pas encore appelé
              </span>
            </div>
          )}
          <div className="font-medium text-sm truncate flex items-center gap-2">
            {/* VAGUE 18d — Badge status coloré (vert/jaune/rouge/neutre) */}
            {biz.status_badge && biz.status_badge.color !== "neutral" && (
              <span
                data-testid={`status-badge-${biz.id}`}
                title={biz.status_badge.tooltip || ""}
                className={
                  "inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider shrink-0 " +
                  (biz.status_badge.color === "green"
                    ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                    : biz.status_badge.color === "yellow"
                    ? "bg-amber-100 text-amber-800 border border-amber-300"
                    : "bg-rose-100 text-rose-800 border border-rose-300")
                }
              >
                <span className={
                  "w-1.5 h-1.5 rounded-full " +
                  (biz.status_badge.color === "green" ? "bg-emerald-500"
                   : biz.status_badge.color === "yellow" ? "bg-amber-500"
                   : "bg-rose-500")
                }/>
                {biz.status_badge.label}
              </span>
            )}
            <span className="truncate">{biz.name}</span>
            <PronounceButton text={biz.name} testid={`pronounce-${biz.id}`} />
            <QualityTierBadge
              tier={biz.quality_tier}
              score={biz.quality_score}
              testid={`quality-badge-${biz.id}`}
            />
          </div>
          <div className="text-[11px] text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
            {biz.category && <span>{biz.category}</span>}
            {biz.city && <><span className="text-border">·</span><MapPin size={10} weight="duotone" className="inline"/> {biz.postal_code} {biz.city}</>}
          </div>
          {/* VAGUE 21 — Coach IA : pastille astuce d'ouverture selon secteur */}
          {biz.coach_hint && (
            <details className="mt-1.5" data-testid={`coach-hint-${biz.id}`}>
              <summary className="cursor-pointer inline-flex items-center gap-1 text-[11px] text-[#8a6a14] hover:text-[#6b4f0a] bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-sm px-1.5 py-0.5 font-medium">
                💡 Astuce {biz.category}
              </summary>
              <div className="mt-1 text-[11px] text-foreground/85 bg-amber-50/70 border border-amber-200 rounded-sm p-2 max-w-md leading-snug">
                {biz.coach_hint}
              </div>
            </details>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
            {phoneHref && (
              <span className="inline-flex items-center gap-1">
                <a href={phoneHref} onClick={() => onStartCall?.()} className="inline-flex items-center gap-1 text-xs font-mono text-primary hover:underline" data-testid={`call-phone-${biz.id}`}>
                  <Phone size={12} weight="bold"/> {biz.phone}
                </a>
                <CopyChip value={biz.phone} testid={`copy-phone-${biz.id}`}/>
              </span>
            )}
            {biz.email && (
              <span className="inline-flex items-center gap-1">
                <button type="button" onClick={() => setMailTo(biz.email)} className="inline-flex items-center gap-1 text-xs font-mono text-primary hover:underline truncate max-w-[180px]" data-testid={`biz-email-${biz.id}`}>
                  <EnvelopeSimple size={12} weight="bold"/> {biz.email}
                </button>
                <CopyChip value={biz.email} testid={`copy-email-${biz.id}`}/>
              </span>
            )}
            <a
              href={`https://www.google.com/search?q=${encodeURIComponent(`${biz.name} ${biz.city || ""} contact`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-foreground/80 hover:text-foreground bg-muted/50 hover:bg-muted border border-border rounded-sm px-2 py-0.5"
              data-testid={`google-search-${biz.id}`}
            >
              <MagnifyingGlass size={11} weight="bold"/> Google
            </a>
            <button
              onClick={onToggleAssist}
              className={`inline-flex items-center gap-1 text-[11px] font-semibold border rounded-sm px-2 py-0.5 transition-colors ${
                assistOpen
                  ? "bg-primary text-primary-foreground border-primary hover:bg-[#0f3d29] hover:text-white"
                  : "bg-primary/10 text-primary border-primary/40 hover:bg-primary/15"
              }`}
              data-testid={`assist-toggle-${biz.id}`}
              aria-expanded={assistOpen}
              title="Ouvre le mode appel : timer, pitch et objections sous les yeux"
            >
              <Headset size={11} weight="bold"/> {assistOpen ? "Fermer assist" : "Mode appel"}
            </button>
            <button
              type="button"
              onClick={() => setHistoryOpen(true)}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-foreground/70 hover:text-foreground bg-muted/40 hover:bg-muted border border-border rounded-sm px-2 py-0.5"
              data-testid={`history-toggle-${biz.id}`}
              title="Voir l’historique CRM de cette entreprise"
            >
              <ClockCounterClockwise size={11} weight="bold"/> Historique
            </button>
            <button
              type="button"
              onClick={() => setAiModalOpen(true)}
              className="inline-flex items-center gap-1 text-[11px] font-bold text-primary-foreground bg-gradient-to-r from-primary to-primary/80 hover:from-[#0f3d29] hover:to-primary border border-primary rounded-sm px-2 py-0.5 shadow-sm"
              data-testid={`ai-help-btn-${biz.id}`}
              title="Assistant IA post-appel : dicte quelques mots, l'IA remplit tout"
            >
              <Sparkle size={11} weight="fill" className="text-yellow-300"/> Aide IA
            </button>
            <button
              type="button"
              onClick={() => setScriptOpen(true)}
              className="inline-flex items-center gap-1 text-[11px] font-bold text-primary bg-primary/10 hover:bg-primary/15 border border-primary/40 rounded-sm px-2 py-0.5"
              data-testid={`sales-script-btn-${biz.id}`}
              title="Accroche, questions, bénéfices, objections, offre et conclusion pour ce prospect"
            >
              <Sparkle size={11} weight="bold"/> Argumentaire IA
            </button>
          </div>

          <FicheShareLinks biz={biz} phone={biz.call.contact_phone || biz.phone} teleproCode={telepro?.code}/>

          {(biz.call.contact_name || biz.call.contact_phone || biz.call.contact_email) && !assistOpen && (
            <div
              className="mt-1.5 inline-flex flex-wrap gap-x-2 gap-y-0.5 items-center text-[11px] bg-primary/5 border border-primary/20 rounded-sm px-2 py-1"
              data-testid={`contact-preview-${biz.id}`}
            >
              {biz.call.contact_name && (
                <span className="inline-flex items-center gap-1 font-medium text-foreground">
                  <User size={10} weight="duotone"/> {biz.call.contact_name}
                </span>
              )}
              {biz.call.contact_phone && (
                <a
                  href={`tel:${biz.call.contact_phone.replace(/\s/g, "")}`}
                  className="inline-flex items-center gap-1 font-mono text-primary hover:underline"
                  data-testid={`contact-preview-phone-${biz.id}`}
                >
                  <Phone size={10} weight="bold"/> {biz.call.contact_phone}
                </a>
              )}
              {biz.call.contact_email && (
                <button
                  type="button"
                  onClick={() => setMailTo(biz.call.contact_email)}
                  className="inline-flex items-center gap-1 font-mono text-primary hover:underline truncate max-w-[180px]"
                  data-testid={`contact-preview-email-${biz.id}`}
                >
                  <EnvelopeSimple size={10} weight="bold"/> {biz.call.contact_email}
                </button>
              )}
              {biz.call.contact_method && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                  · contacté par {biz.call.contact_method === "both" ? "📞+📧" : biz.call.contact_method === "email" ? "📧 email" : "📞 tél."}
                </span>
              )}
            </div>
          )}

          {isEditingNote ? (
            <div className="mt-2 flex flex-col gap-1.5">
              <div className="flex items-start gap-2">
                <textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value.slice(0, 500))}
                  rows={2}
                  className="flex-1 text-xs border border-border rounded-sm px-2 py-1 bg-background focus:border-primary outline-none"
                  placeholder="Objection, date de rappel, prénom décideur…"
                  autoFocus
                  data-testid={`note-input-${biz.id}`}
                />
                <div className="flex flex-col gap-1">
                  <MicButton
                    testid={`note-mic-${biz.id}`}
                    onAppend={(chunk) => {
                      const cleaned = chunk.trim();
                      if (!cleaned) return;
                      setNoteDraft((cur) => {
                        const base = (cur || "").trim();
                        const merged = base ? `${base} ${cleaned}` : cleaned;
                        return merged.slice(0, 500);
                      });
                    }}
                  />
                  <button onClick={() => saveNote(noteDraft)} className="text-[11px] bg-primary text-primary-foreground px-2 py-1 rounded-sm font-bold" data-testid={`note-save-${biz.id}`}>OK</button>
                  <button onClick={() => { setNoteDraft(biz.call.note || ""); onCancelEditNote(); }} className="text-[11px] text-muted-foreground px-2 py-1">×</button>
                </div>
              </div>
              {/* Vague 6 — Templates de notes rapides */}
              <div className="flex flex-wrap gap-1" data-testid={`note-templates-${biz.id}`}>
                {NOTE_TEMPLATES.map((tpl) => (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => {
                      const cur = noteDraft.trim();
                      const next = cur ? `${cur} · ${tpl.text}` : tpl.text;
                      setNoteDraft(next.slice(0, 500));
                    }}
                    className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground hover:text-primary bg-muted/40 hover:bg-primary/10 border border-border rounded-sm px-1.5 py-0.5 transition-colors"                    data-testid={`note-template-${tpl.id}-${biz.id}`}
                    title={tpl.text}
                  >
                    + {tpl.label}
                  </button>
                ))}
              </div>
              {suggestion?.status && suggestion.status !== biz.call.status && (
                <button
                  type="button"
                  onClick={() => { saveNote(noteDraft); setStatus(suggestion.status); }}
                  className="self-start inline-flex items-center gap-1.5 text-[11px] font-bold text-blue-800 bg-blue-50 border border-blue-400 rounded-sm px-2.5 py-1.5 hover:bg-blue-100 transition-colors shadow-sm"
                  data-testid={`status-suggestion-${biz.id}`}
                  title={`IA détecte : ${(suggestion.matched || []).join(" · ") || "situation reconnue"}`}
                >
                  <span>👉 Clique sur </span>
                  <span className="uppercase tracking-wide">{suggestion.label}</span>
                  <span className="text-[9px] text-blue-600 font-normal">— appliquer</span>
                </button>
              )}
              {/* Vague 11-D — Coach objection live */}
              <ObjectionCoachChip
                note={noteDraft}
                biz={biz}
                testid={`objection-coach-${biz.id}`}
              />
            </div>
          ) : biz.call.note ? (
            <button onClick={() => { setNoteDraft(biz.call.note); onStartEditNote(); }} className="mt-1 text-[11px] text-foreground/70 italic bg-muted/40 px-2 py-1 rounded-sm hover:bg-muted text-left max-w-full" data-testid={`note-display-${biz.id}`}>
              <PencilSimple size={10} weight="duotone" className="inline mr-1"/> {biz.call.note}
            </button>
          ) : (
            <button onClick={onStartEditNote} className="mt-1 text-[10px] text-muted-foreground hover:text-foreground" data-testid={`note-add-${biz.id}`}>+ Ajouter une note</button>
          )}

          {biz.call.status === "callback" && (
            <div className="mt-2 space-y-1.5">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground block mb-0.5">Rappeler le :</label>
                <input
                  type="datetime-local"
                  value={toLocalInput(biz.call.callback_at)}
                  onChange={(e) => saveCallback(e.target.value ? new Date(e.target.value).toISOString() : null)}
                  className="text-xs border border-border rounded-sm px-2 py-1 bg-background focus:border-primary outline-none"
                  data-testid={`callback-input-${biz.id}`}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground block mb-0.5">Raison du rappel :</label>
                <input
                  type="text"
                  placeholder="ex: responsable dispo l'après-midi"
                  defaultValue={biz.call.callback_reason || ""}
                  onBlur={(e) => onUpdate({ callback_reason: e.target.value })}
                  className="text-xs border border-border rounded-sm px-2 py-1 bg-background focus:border-primary outline-none w-full max-w-xs"
                  data-testid={`callback-reason-${biz.id}`}
                />
              </div>
              <CallbackSuggestion biz={biz} availability={`${biz.call.callback_reason || ""} ${biz.call.note || ""}`} onApply={saveCallback}/>
            </div>
          )}
        </div>

        <div className="flex flex-col items-stretch sm:items-end gap-1.5 shrink-0">
          {biz.call.status === "to_call" && (
            <div className="flex gap-2 flex-wrap" data-testid={`quick-actions-${biz.id}`}>
              <div className="inline-flex items-center gap-1">
                <QuickBtn onClick={() => setStatus("no_answer", true)} label="Pas joint" testid={`quick-no-answer-${biz.id}`} colorCls="hover:bg-muted-foreground/15 text-muted-foreground border-muted-foreground/30">
                  <PhoneSlash size={13} weight="bold"/>
                </QuickBtn>
                <ActionInfoPopover
                  title="Quand utiliser « Pas joint » ?"
                  accentColor="muted"
                  testidPrefix={`info-no-answer-${biz.id}`}
                >
                  <p className="mb-2">
                    Le commerçant <b>n&apos;a pas décroché</b>, tu es tombé(e) sur un répondeur,
                    ou la ligne est occupée.
                  </p>
                  <div className="font-bold mb-1">Cas typiques :</div>
                  <ul className="list-disc list-inside space-y-0.5 mb-2">
                    <li>Sonne mais personne ne répond.</li>
                    <li>Messagerie vocale — laisser un message court est OK.</li>
                    <li>Occupé, appel coupé.</li>
                    <li>Horaires probablement fermés (ex: appelé à midi un restaurant).</li>
                  </ul>
                  <div className="font-bold mb-1 text-primary">Bonne pratique :</div>
                  <p className="mb-2">
                    Tu peux <b>rappeler la même entreprise à un autre horaire</b> plus tard dans la journée.
                    Elle reste dans ta liste et pourra être re-tentée demain.
                  </p>
                  <div className="font-bold mb-1 text-secondary">⚠️ À NE PAS utiliser pour :</div>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>Un numéro <b>invalide/faux</b> → utilise le menu <b>Statut → Faux numéro</b>.</li>
                    <li>Un refus <b>franc</b> par téléphone → utilise <b>Refus</b>.</li>
                  </ul>
                </ActionInfoPopover>
              </div>
              <div className="inline-flex items-center gap-1">
                <QuickBtn onClick={() => setStatus("refused", true)} label="Refus" testid={`quick-refused-${biz.id}`} colorCls="hover:bg-secondary/15 text-secondary border-secondary/40">
                  <XCircle size={13} weight="bold"/>
                </QuickBtn>
                <ActionInfoPopover
                  title="Quand utiliser « Refus » ?"
                  accentColor="secondary"
                  testidPrefix={`info-refused-${biz.id}`}
                >
                  <p className="mb-2">
                    Le commerçant a <b>refusé aujourd&apos;hui</b> mais reste joignable et
                    poli — pas de menace, pas de « ne me rappelez plus jamais ».
                  </p>
                  <div className="font-bold mb-1">Cas typiques :</div>
                  <ul className="list-disc list-inside space-y-0.5 mb-2">
                    <li>« Ça ne m&apos;intéresse pas. »</li>
                    <li>« Je suis déjà chez un concurrent. »</li>
                    <li>« Je n&apos;ai pas le temps aujourd&apos;hui, non merci. »</li>
                    <li>« Envoyez-moi juste un email si vous voulez. » (note-le en note et passe à Refus).</li>
                  </ul>
                  <div className="font-bold mb-1 text-primary">Bon à savoir :</div>
                  <p className="mb-2">
                    La fiche peut <b>revenir dans les listes</b> plus tard (dans plusieurs mois)
                    si aucun autre télépro ne l&apos;a rappelée entre-temps.
                  </p>
                  <div className="font-bold mb-1 text-secondary">⚠️ À NE PAS utiliser pour :</div>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>« Ne me rappelez PLUS JAMAIS » → utilise <b>Ne plus contacter</b> (RGPD).</li>
                    <li>« Rappelez-moi demain matin » → utilise <b>Rappeler</b>.</li>
                  </ul>
                </ActionInfoPopover>
              </div>
              <div className="inline-flex items-center gap-1">
                <QuickBtn onClick={() => setStatus("callback")} label="Rappeler" testid={`quick-callback-${biz.id}`} colorCls="hover:bg-[#C8795A]/15 text-[#C8795A] border-[#C8795A]/40">
                  <ClockCountdown size={13} weight="fill"/>
                </QuickBtn>
                <ActionInfoPopover
                  title="Quand utiliser « Rappeler » ?"
                  accentColor="warm"
                  testidPrefix={`info-callback-${biz.id}`}
                >
                  <p className="mb-2">
                    Le commerçant a <b>demandé qu&apos;on le rappelle</b> à un autre moment.
                    C&apos;est un <b>signal chaud</b> — ne le laisse pas s&apos;éteindre.
                  </p>
                  <div className="font-bold mb-1">Cas typiques :</div>
                  <ul className="list-disc list-inside space-y-0.5 mb-2">
                    <li>« Rappelez-moi demain à 14h. »</li>
                    <li>« Je regarde ça ce soir, appelez-moi vendredi. »</li>
                    <li>« C&apos;est le patron qui décide, il est là mardi. »</li>
                  </ul>
                  <div className="font-bold mb-1 text-primary">Bonne pratique :</div>
                  <ul className="list-disc list-inside space-y-0.5 mb-2">
                    <li>Note la <b>date + heure exacte</b> dans le champ note (« Rappel mardi 15h — parler à Marc »).</li>
                    <li>Note aussi le <b>prénom du décideur</b> si tu l&apos;as.</li>
                    <li>Rappelle à l&apos;heure demandée — c&apos;est ce qui fait la différence sur la vente.</li>
                  </ul>
                  <div className="font-bold mb-1 text-secondary">⚠️ Attention :</div>
                  <p>
                    Ne mets pas « Rappeler » pour <b>reporter à demain</b> systématiquement.
                    Utilise-le uniquement quand le commerçant l&apos;a demandé.
                  </p>
                </ActionInfoPopover>
              </div>
            </div>
          )}

          {/* Ne plus contacter — blocage universel (cahier de modifs 2026-02) */}
          <div className="relative flex items-center gap-1.5 self-end sm:self-auto">
            <button
              type="button"
              onClick={markDnc}
              className="text-[10px] uppercase tracking-wider font-bold text-secondary/80 hover:text-secondary hover:underline inline-flex items-center gap-1"
              data-testid={`dnc-btn-${biz.id}`}
              title="Bloque cette entreprise pour tous les téléprospecteurs"
            >
              <XCircle size={11} weight="bold"/> Ne plus contacter
            </button>
            <ActionInfoPopover
              title="À quoi sert « Ne plus contacter » ?"
              accentColor="secondary"
              testidPrefix={`dnc-info-${biz.id}`}
            >
              <p className="mb-2">
                Ce bouton enregistre officiellement que ce commerçant <b>ne veut plus être démarché</b>{" "}
                pour MonCommerce68. Ce n&apos;est pas un refus classique — c&apos;est une <b>opposition RGPD</b>{" "}
                (art. 21).
              </p>
              <div className="font-bold mb-1">Concrètement, dès que tu cliques :</div>
              <ul className="list-disc list-inside space-y-0.5 mb-2">
                <li>L&apos;entreprise <b>disparaît de toutes les futures listes d&apos;appels</b> (tous les télépros).</li>
                <li>Le <b>numéro et l&apos;email</b> sont ajoutés à la liste d&apos;opposition — aucun email automatique ne partira plus.</li>
                <li>Un badge « Fermé au démarchage » apparaît sur la fiche publique.</li>
                <li>Une trace horodatée est enregistrée (obligation légale en cas de contrôle CNIL).</li>
              </ul>
              <div className="font-bold mb-1">Quand l&apos;utiliser ?</div>
              <ul className="list-disc list-inside space-y-0.5 mb-2">
                <li>Le commerçant dit « <b>ne me rappelez plus</b> » ou « <b>retirez-moi de vos fichiers</b> ».</li>
                <li>Il menace de porter plainte ou d&apos;écrire à la CNIL.</li>
                <li>Il refuse fermement toute prospection future.</li>
              </ul>
              <div className="font-bold mb-1 text-secondary">⚠️ À NE PAS utiliser pour :</div>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Un simple « pas intéressé aujourd&apos;hui » → bouton <b>Refus</b>.</li>
                <li>« Rappelez-moi plus tard » → bouton <b>Rappeler</b>.</li>
                <li>Numéro qui ne répond pas → bouton <b>Pas joint</b>.</li>
              </ul>
              <div className="mt-3 pt-2 border-t border-border text-[10px] text-muted-foreground italic">
                Une fois cliqué, seule l&apos;administration MonCommerce68 peut lever ce blocage
                (si le commerçant change d&apos;avis et nous contacte).
              </div>
            </ActionInfoPopover>
          </div>

          <div className="relative">
            <button
              onClick={() => setShowStatusMenu((v) => !v)}
              className={`inline-flex items-center gap-1.5 border rounded-sm px-3 py-1.5 text-xs w-full sm:w-auto justify-center ${status.color}`}
              data-testid={`status-btn-${biz.id}`}
              aria-haspopup="true"
              aria-expanded={showStatusMenu}
            >
              <Icon size={12} weight={status.id === "sale" ? "fill" : "bold"}/>
              {status.label}
              {biz.call.refusal_reason && (
                <span className="normal-case font-normal" data-testid={`refusal-reason-${biz.id}`}>
                  · {biz.call.refusal_reason === "no_more_contact" ? "ne plus appeler"
                    : (REFUSAL_OPTIONS.find((o) => o.id === biz.call.refusal_reason)?.label || "").toLowerCase()}
                </span>
              )}
              {biz.call.hot_lead_offer && (
                <span className="normal-case font-normal" data-testid={`hot-lead-offer-${biz.id}`}>
                  · {biz.call.hot_lead_offer === "premium_ia" ? "Premium IA" : "Premium"}
                </span>
              )}
              <CaretDown size={10} weight="bold"/>
            </button>
            {showStatusMenu && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowStatusMenu(false)}/>
                <div className="absolute right-0 mt-1 z-[80] bg-card border border-border rounded-sm shadow-lg w-48 py-1" data-testid={`status-menu-${biz.id}`}>
                  {STATUSES.map((s) => {
                    const SIcon = s.icon;
                    const active = s.id === biz.call.status;
                    return (
                      <button
                        key={s.id}
                        onClick={() => { setStatus(s.id); setShowStatusMenu(false); }}
                        className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-accent ${active ? "font-bold" : ""}`}
                        data-testid={`status-opt-${biz.id}-${s.id}`}
                      >
                        <SIcon size={12} weight={s.id === "sale" ? "fill" : "regular"}/>
                        {s.label}
                        {active && <CheckCircle size={12} weight="fill" className="ml-auto text-primary"/>}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {assistOpen && (
        <AssistPanel
          biz={biz}
          telepro={telepro}
          onSetStatus={(id) => setStatus(id)}
          onQualify={(patch) => onUpdate(patch)}
          onSaveNote={(note) => onUpdate({ note })}
          onSaveContact={(patch) => onUpdate(patch)}
          onClose={onToggleAssist}
        />
      )}
      {qualify && (
        <QualifyStatusModal
          status={qualify.status}
          bizName={biz.name}
          bizId={biz.id}
          onClose={() => setQualify(null)}
          onPick={(patch) => { onUpdate(patch, { jumpNext: qualify.jumpNext }); setQualify(null); }}
        />
      )}
      {scriptOpen && <SalesScriptModal biz={biz} onClose={() => setScriptOpen(false)}/>}
      {webLead && (
        <WebsiteLeadModal
          biz={biz}
          onClose={() => setWebLead(null)}
          onSubmit={(patch) => { onUpdate(patch, { jumpNext: webLead.jumpNext }); setWebLead(null); }}
        />
      )}
      {mailTo && (
        <EmailPreviewModal
          biz={biz}
          telepro={telepro}
          contactName={biz.call.contact_name}
          contactEmail={mailTo}
          onClose={() => setMailTo("")}
          onSent={() => {
            if (!biz.call.contact_method) onUpdate({ contact_method: "email" });
            setMailTo("");
          }}
        />
      )}
      <CrmHistoryModal
        businessId={biz.id}
        businessName={biz.name}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
      <PostCallAIModal
        open={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        biz={biz}
        onApply={(patch) => onUpdate(patch)}
      />
    </li>
  );
}
